import { DayOfWeek, PlannedMenuDisabledDaysInput, User } from "@notemeal/graphql-types";
import {
  AthletesBirthdaysFragment,
  MenuBuilderMenuItemWithMacrosFragment,
  usePlannedMenuHeaderLazyQuery,
  useUnlockPlannedMenuMutation,
} from "apps/web/src/types";
import { useLocalStorageValueAsType } from "apps/web/src/utils/localStorage";
import { useUser } from "apps/web/src/utils/tokens";
import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Boolean } from "runtypes";
import { MenuBuilderMealRowItemType } from "./Meal/MenuBuilderMealSchema";
import { DAY_CELL_WIDTH, LEFT_PANEL_CELL_WIDTH } from "./Meal/constants";
import { MenuBuilderWeek, getDays, transformWeeks } from "./utils";

type Clipboard =
  | {
      type: "cell";
      formPath: string;
      items: MenuBuilderMealRowItemType[];
    }
  | {
      type: "row";
      formPath: string;
      items: MenuBuilderMealRowItemType[];
      diningStationName: string;
      foodType: string | null;
    };
type MenuBuilderGridContextValue = {
  loading: boolean;
  menuId: string;
  menuName: string;
  menuLastEditedAt?: string;
  selectedWeekIndex: number;
  setSelectedWeekIndex: (index: number) => void;
  weeks: readonly MenuBuilderWeek[];
  days: DayOfWeek[];
  mealGridWidth: string;
  mealsToDelete: string[];
  setMealsToDelete: (mealIds: string[]) => void;
  handleDeleteMealSideEffects: (mealFormIndex: number) => void;
  rowsToDelete: string[];
  setRowsToDelete: (menuItems: string[]) => void;
  rowItemsToDelete: string[];
  setRowItemsToDelete: (menuItems: string[]) => void;
  clipboard?: Clipboard;
  setClipboard: (clipboard?: Clipboard) => void;
  disabledDays: PlannedMenuDisabledDaysInput[];
  setDisabledDays: (days: PlannedMenuDisabledDaysInput[]) => void;
  selectedDay?: DayOfWeek;
  setSelectedDay: (day?: DayOfWeek) => void;
  athleteBirthdays: readonly AthletesBirthdaysFragment[];
  // track meals that get edited, to workaround rhf not correctly identifying dirty fields
  updatedFormMeals: `weeks.${number}.meals.${number}`[];
  markMealAsUpdated: (formId: `weeks.${number}.meals.${number}`) => void;
  markMealsAsUpdated: (formIds: `weeks.${number}.meals.${number}`[]) => void;
  clearUpdatedMealsForWeek: (weekIndex: number) => void;
  clearUpdatedMeals: () => void;
  // menu locking
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  currentUserHasLock: boolean;
  setCurrentUserHasLock: (currentUserHasLock: boolean) => void;
  lockCreatedAt?: string;
  lockCreatedBy?: Pick<User, "firstName" | "lastName">;
  displayMacros: boolean;
  setDisplayMacros: (displayMacros: boolean) => void;
  menuItemDetailsById: Record<string, MenuBuilderMenuItemWithMacrosFragment>;
  setMenuItemDetailsById: React.Dispatch<React.SetStateAction<Record<string, MenuBuilderMenuItemWithMacrosFragment>>>;
};

const defaultState = {
  loading: true,
  menuId: "",
  menuName: "",
  menuLastEditedAt: undefined,
  selectedWeekIndex: 0,
  setSelectedWeekIndex: () => {},
  weeks: [],
  days: [],
  mealGridWidth: `calc(${LEFT_PANEL_CELL_WIDTH} + 5 * ${DAY_CELL_WIDTH} + 2px)`,
  mealsToDelete: [],
  setMealsToDelete: () => {},
  handleDeleteMealSideEffects: () => {},
  rowsToDelete: [],
  setRowsToDelete: () => {},
  rowItemsToDelete: [],
  setRowItemsToDelete: () => {},
  clipboard: undefined,
  setClipboard: () => {},
  disabledDays: [],
  setDisabledDays: () => {},
  selectedDay: undefined,
  setSelectedDay: () => {},
  athleteBirthdays: [],
  updatedFormMeals: [],
  markMealAsUpdated: () => {},
  markMealsAsUpdated: () => {},
  clearUpdatedMealsForWeek: () => {},
  clearUpdatedMeals: () => {},
  isEditable: true,
  setIsEditable: () => {},
  currentUserHasLock: false,
  setCurrentUserHasLock: () => {},
  lockCreatedMinutesAgo: undefined,
  lockCreatedBy: undefined,
  displayMacros: false,
  setDisplayMacros: () => {},
  menuItemDetailsById: {},
  setMenuItemDetailsById: () => {},
};
const MenuBuilderGridPageContext = createContext<MenuBuilderGridContextValue>(defaultState);

export const MenuBuilderProvider: React.FC<{ plannedMenuId: string; children: ReactNode }> = ({ plannedMenuId, children }) => {
  // on first render get the menu, and any time the menu is fetched refetch its meals
  const [getPlannedMenu, { data: menu, loading: loadingMenu }] = usePlannedMenuHeaderLazyQuery({
    variables: { plannedMenuId },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    getPlannedMenu();
  }, [getPlannedMenu]);

  const [unlockPlannedMenu] = useUnlockPlannedMenuMutation({ variables: { input: { plannedMenuId } } });
  const history = useHistory();
  const currentUser = useUser();

  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [mealsToDelete, setMealsToDelete] = useState<string[]>([]);
  const [rowsToDelete, setRowsToDelete] = useState<string[]>([]);
  const [rowItemsToDelete, setRowItemsToDelete] = useState<string[]>([]);
  const [clipboard, setClipboard] = useState<Clipboard | undefined>(undefined);
  const [disabledDays, setDisabledDays] = useState<PlannedMenuDisabledDaysInput[] | undefined>(undefined);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | undefined>(undefined);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [currentUserHasLock, setCurrentUserHasLock] = useState<boolean>(false);
  const [updatedFormMeals, setUpdatedFormMeals] = useState<`weeks.${number}.meals.${number}`[]>([]);
  const [displayMacros, setDisplayMacros] = useLocalStorageValueAsType("menuBuilderDisplayMacros", Boolean);
  const [menuItemDetailsById, setMenuItemDetailsById] = useState<Record<string, MenuBuilderMenuItemWithMacrosFragment>>({});

  // dedupe all the duplicates we get from onChange
  const markMealAsUpdated = (formId: `weeks.${number}.meals.${number}`) => {
    setUpdatedFormMeals([...new Set(updatedFormMeals).add(formId)]);
  };

  // batch version for adding multiple meals at once (e.g., when copying a week)
  const markMealsAsUpdated = (formIds: `weeks.${number}.meals.${number}`[]) => {
    setUpdatedFormMeals(prev => {
      const updatedSet = new Set(prev);
      formIds.forEach(id => updatedSet.add(id));
      return [...updatedSet];
    });
  };

  // clear all tracked meal paths for a specific week (used when replacing a week's content)
  const clearUpdatedMealsForWeek = (weekIndex: number) => {
    setUpdatedFormMeals(prev => {
      const weekPrefix = `weeks.${weekIndex}.meals.`;
      return prev.filter(path => !path.startsWith(weekPrefix));
    });
  };

  const handleDeleteMealSideEffects = (mealFormIndexToDelete: number) => {
    // remove the meal from the list of updated meals
    const filteredMeals = Array.from(updatedFormMeals).filter(m => getMealIndex(m) !== mealFormIndexToDelete);

    const updatedMeals = filteredMeals.map(m => updateMealIndex(m, mealFormIndexToDelete));
    setUpdatedFormMeals(updatedMeals);
  };

  // unlock menu when user navigates away
  useEffect(() => {
    if (currentUserHasLock) {
      const removeListener = history.listen(() => {
        unlockPlannedMenu();
      });
      return removeListener;
    }
    return () => {};
  }, [history, currentUserHasLock, unlockPlannedMenu]);

  // assertion to avoid null checks everywhere
  if (!menu?.plannedMenu || menu.plannedMenu.weeks.length === 0) {
    return <MenuBuilderGridPageContext.Provider value={defaultState}>{children}</MenuBuilderGridPageContext.Provider>;
  }

  const days = getDays(menu?.plannedMenu.occurrence);
  // keeps same width when meal is collapsed as when its expanded
  const mealGridWidth = `calc(${LEFT_PANEL_CELL_WIDTH} + ${days.length} * ${DAY_CELL_WIDTH} + 2px)`;
  if (disabledDays === undefined) {
    setDisabledDays(
      menu.plannedMenu.weeks.map(week => {
        return {
          plannedMenuWeekId: week.id,
          disabledDays: week.disabledDays as DayOfWeek[],
        };
      })
    );
  }

  // currently locks are specific to the user, instead of specific to a user session. If multi-tab stomping happens a lot we may need to change this
  if (currentUserHasLock !== !!(menu.plannedMenu.lockCreatedBy?.id && menu.plannedMenu.lockCreatedBy.id === currentUser?.id)) {
    setCurrentUserHasLock(!!(menu.plannedMenu.lockCreatedBy?.id && menu.plannedMenu.lockCreatedBy.id === currentUser?.id));
  }

  if (isEditable !== (menu.plannedMenu.lockCreatedAt === null || currentUserHasLock)) {
    setIsEditable(menu.plannedMenu.lockCreatedAt === null || currentUserHasLock);
  }

  return (
    <MenuBuilderGridPageContext.Provider
      value={{
        loading: loadingMenu,
        menuId: plannedMenuId,
        menuName: menu.plannedMenu.name,
        menuLastEditedAt: menu.plannedMenu.lastEditedAt ?? undefined,
        selectedWeekIndex,
        setSelectedWeekIndex,
        weeks: transformWeeks(menu.plannedMenu.weeks, menu.plannedMenu),
        days,
        mealGridWidth,
        mealsToDelete,
        setMealsToDelete,
        handleDeleteMealSideEffects,
        rowsToDelete,
        setRowsToDelete,
        rowItemsToDelete,
        setRowItemsToDelete,
        clipboard,
        setClipboard,
        disabledDays: disabledDays ?? [],
        setDisabledDays,
        selectedDay,
        setSelectedDay,
        athleteBirthdays: menu.plannedMenu.athletesWithBirthdaysInRange || [],
        updatedFormMeals,
        markMealAsUpdated,
        markMealsAsUpdated,
        clearUpdatedMealsForWeek,
        clearUpdatedMeals: () => setUpdatedFormMeals([]),
        isEditable,
        setIsEditable,
        currentUserHasLock,
        setCurrentUserHasLock,
        lockCreatedAt: menu.plannedMenu.lockCreatedAt ?? undefined,
        lockCreatedBy: menu.plannedMenu.lockCreatedBy ?? undefined,
        displayMacros,
        setDisplayMacros,
        menuItemDetailsById,
        setMenuItemDetailsById,
      }}
    >
      {children}
    </MenuBuilderGridPageContext.Provider>
  );
};

export const useMenuBuilderContext = () => {
  const state = useContext(MenuBuilderGridPageContext);
  return state;
};

const getMealIndex = (mealFormId: `weeks.${number}.meals.${number}`) => parseInt(mealFormId.charAt(mealFormId.length - 1));

// manually update the meal index for the meal(s) when they are after the removed index
// this is required since we manually track updated meals by index, but they are reindexed when a meal is removed
const updateMealIndex = (mealFormId: `weeks.${number}.meals.${number}`, mealFormIndexToDelete: number) => {
  const parts = mealFormId.split(".");
  const mealIndex = getMealIndex(mealFormId);
  if (mealIndex > mealFormIndexToDelete) {
    //this sets the meal index for the reindexed meal(s)
    parts[parts.length - 1] = (mealIndex - 1).toString();
  }
  return parts.join(".") as `weeks.${number}.meals.${number}`;
};
